/* eslint-disable import/no-extraneous-dependencies */
import React, { useContext, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { TextField, MenuItem } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import './bussines-historic-form.scss';
import dayjs from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import * as z from 'zod';

import 'dayjs/locale/es';
import Button from '@/components/atoms/button/button';
import GlobalContext from '@/contexts/globalContext';
import UseVerificationDigit from '@/hooks/useVerificationDigit';
import { getHistoricalAfiliation } from '@/services/certificate-services';
import useBrowserMode from '@/shared/hooks/useBrowserMode';

import listTypeDoc from '../../../utils/typeDocLegal.json';
import ErrorModal from '../error-modal/errorModal';
import PdfViewModal from '../pdf-view-modal/pdfViewModal';

const FormSchema = z
  .object({
    documentType: z
      .string()
      .nonempty('Este campo es obligatorio')
      .refine((val) => {
        return listTypeDoc.some((doc) => doc.id === val);
      }, 'Tipo de documento inválido'),

    documentNumber: z.string().nonempty('Este campo es obligatorio'),

    startDate: z
      .instanceof(dayjs)
      .refine((date) => date.isValid(), 'Fecha inicial inválida')
      .nullable()
      .transform((date) => date || null)
      .refine((date) => date !== null, 'La fecha inicial es obligatoria'),

    endDate: z
      .instanceof(dayjs)
      .refine((date) => date.isValid(), 'Fecha final inválida')
      .nullable()
      .transform((date) => date || null)
      .refine((date) => date !== null, 'La fecha final es obligatoria'),
  })
  .refine(
    (data) => {
      if (data.startDate && data.endDate) {
        return !data.endDate.isBefore(data.startDate);
      }
      return true;
    },
    {
      path: ['endDate'],
      message: 'La fecha final debe ser igual o mayor a la fecha inicial',
    },
  );

type FormValues = z.infer<typeof FormSchema>;

const BussinesHistoricForm: React.FC = ({ dataQuery }) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      documentType: '',
      documentNumber: '',
      startDate: null,
      endDate: null,
    },
  });

  const { business } = useContext(GlobalContext);
  const { localStorage } = useBrowserMode();
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [openModalPDF, setOpenModalPDF] = useState(false);
  const [docData, setDocData] = useState<string>('');
  const currentTab = localStorage.getItem('currentTab');

  const allowToUpload = business?.get?.serviceId.includes(`${dataQuery.serviceId}`);
  const isPersonasTab = currentTab === 'PERSONAS';
  const isDisabled = !isPersonasTab && (!business?.get || !allowToUpload);

  const onSubmit = async (data: FormValues) => {
    setLoading(true);

    try {
      const numDocumentCompany =
        currentTab !== 'personas' && business?.get?.companyDocID
          ? business.get.companyDocID +
            (business.get.companyDocType === 'CO1N'
              ? UseVerificationDigit(String(business.get.companyDocID))
              : '')
          : undefined;

      const typeDocumentCompany =
        currentTab !== 'personas' && business?.get
          ? listTypeDoc.find((item) => item.typaDocSAP === business?.get.companyDocType)?.id || ''
          : undefined;

      const response = await getHistoricalAfiliation(
        data.documentNumber,
        data.documentType,
        numDocumentCompany,
        typeDocumentCompany,
        data.startDate ? data.startDate.format('YYYY-MM-DD') : '',
        data.endDate ? data.endDate.format('YYYY-MM-DD') : '',
      );

      if (response.status === 200) {
        if (response.data.status === 200) {
          setDocData(response.data.data.certificadoB64 || response.data.data.certificateB64);
          setOpenModalPDF(true);
        } else if (response.data.status === 400) {
          setOpenModalError(true);
        } else {
          setOpenModal(true);
        }
      }
    } catch (error) {
      console.error('Error al obtener la afiliación histórica:', error);
      setOpenModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <form className="form-document" onSubmit={handleSubmit(onSubmit)}>
        <div className="form-document__group">
          <label htmlFor="documentType" className="form-document__label">
            Tipo de documento
          </label>
          <Controller
            disabled={isDisabled}
            name="documentType"
            control={control}
            render={({ field }) => (
              <TextField
                variant="standard"
                select
                placeholder="Tipo de documento"
                fullWidth
                error={!!errors.documentType}
                {...field}
              >
                {listTypeDoc.map((doc, index) => (
                  <MenuItem key={`${doc.id}-${index}`} value={doc.id}>
                    {doc.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          {errors.documentType && (
            <p className="form-document__error">{errors.documentType.message}</p>
          )}
        </div>

        <div className="form-document__group">
          <label htmlFor="documentNumber" className="form-document__label">
            Número de documento
          </label>
          <Controller
            name="documentNumber"
            control={control}
            render={({ field }) => (
              <TextField
                disabled={isDisabled}
                className="form-document__input"
                id="documentNumber"
                variant="standard"
                fullWidth
                error={!!errors.documentNumber}
                {...field}
              />
            )}
          />
          {errors.documentNumber && (
            <p className="form-document__error">{errors.documentNumber.message}</p>
          )}
        </div>

        <div className="form-document__group">
          <label className="form-document__labelbig">Selecciona el rango de fecha</label>

          <div className="form-document__dates">
            <div className="form-document__date">
              <label className="form-document__date-label">
                Fecha inicial <span className="form-document__error">*</span>
              </label>
              <div className="form-document__date-input">
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      disabled={!business?.get}
                      slotProps={{
                        openPickerButton: {
                          sx: { color: '#EE2B7B' },
                        },
                      }}
                      format="DD/MM/YYYY"
                      minDate={dayjs().subtract(10, 'years')}
                      maxDate={dayjs()}
                      value={field.value || null}
                      onChange={(date) => {
                        field.onChange(date);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.startDate}
                          helperText={errors.startDate ? errors.startDate.message : ''}
                          variant="standard"
                        />
                      )}
                    />
                  )}
                />
              </div>
              {errors.startDate && (
                <p className="form-document__error">{errors.startDate.message}</p>
              )}
            </div>

            <div className="form-document__date">
              <label className="form-document__date-label">
                Fecha final <span className="form-document__error">*</span>
              </label>
              <div className="form-document__date-input">
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      disabled={!business?.get}
                      slotProps={{
                        openPickerButton: {
                          sx: { color: '#EE2B7B' },
                        },
                      }}
                      format="DD/MM/YYYY"
                      minDate={dayjs().subtract(10, 'years')}
                      maxDate={dayjs()}
                      value={field.value || null}
                      onChange={(date) => field.onChange(date)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.endDate}
                          helperText={errors.endDate ? errors.endDate.message : ''}
                          variant="standard"
                        />
                      )}
                    />
                  )}
                />
              </div>
              {errors.endDate && <p className="form-document__error">{errors.endDate.message}</p>}
            </div>
          </div>
        </div>

        {openModal && (
          <ErrorModal buttonCopy="Aceptar" isMobile closeModal={() => setOpenModal(false)} />
        )}
        {openModalError && (
          <ErrorModal
            title="¡Ups! No encontramos una coincidencia"
            subtitle="Por favor, revise los datos ingresados y la empresa con la que inició sesión e inténtelo de nuevo."
            closeModal={() => setOpenModalError(false)}
            isMobile
            buttonCopy="ACEPTAR"
          />
        )}
        {openModalPDF && (
          <PdfViewModal
            base64={docData}
            selectedFileName="Historico de afiliación empresas"
            setActiveModal={setOpenModalPDF}
          />
        )}

        <Button
          classname={
            isDisabled || loading ? 'form-document__submitDisable' : 'form-document__submit'
          }
          isDisabled={isDisabled || loading}
          text={loading ? 'Generando...' : 'Generar'}
        />
        {isDisabled && (
          <p className="form-document__error">
            Debes seleccionar una empresa y tener habilitados tus permisos empresariales
          </p>
        )}
      </form>
    </LocalizationProvider>
  );
};

export default BussinesHistoricForm;
